// 261010 Epic 2 / P2b-1 — bước STORYBOARD của một video, CÓ thao tác (khung mới). Tái dùng `StoryboardBlock` (nhận `ev` + `ctx` độc lập, không cần EventPlanTab).
// Ẩn giá (`hideCost`), luôn mở, lỗi nghiệp vụ báo ra khối bước. Thao tác Xoá thêm ở đây (widget cũ không có). Quy tắc hiển thị/bật: `workflowActionPolicy`.
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Popconfirm, Space, Spin, Alert } from 'antd';
import { filmApi } from '../../services/api/film';
import type { FilmEvent, PositionMapsResponse, StoryboardListItem } from '../../types/film';
import type { AutoPrompt } from '../../types/filmWorkflow';
import { autoPromptLineOf } from './workflowRunPolicy';
import { useAliveRef } from '../../hooks/useAliveRef';
import StoryboardCaptions from './StoryboardCaptions';
import StoryboardBlock, { type StoryboardCtx } from './StoryboardBlock';
import { actionOf, actionsFor, confirmContent, runningKindsOf, storyboardCtxOf, workflowActionError, type ActionError, type WorkflowActionCtx } from './workflowActionPolicy';
import { RunningTag, StepErrorAlert, type InteractiveProps } from './WorkflowStepShared';

interface Props extends InteractiveProps {
  eventId: string;
  ev: FilmEvent;
  storyboards: StoryboardListItem[] | null;
  storyboardsError: string | null;
  maps: PositionMapsResponse | null;
  /** 261030 B2 — khung 4 bước: duyệt tấm không tự dựng prompt (bước ④ làm). Không truyền ⇒ y như cũ. */
  noAutoPrompt?: boolean;
  /** 261030 B2 — dòng tiền thêm vào hộp xác nhận «Tạo tấm» / «AI soạn» (ẩn giá ở khung 4 bước). */
  genCostNote?: string;
  composeCostNote?: string;
}

const WorkflowStoryboardStep: React.FC<Props> = ({ eventId, ev, storyboards, storyboardsError, maps, noAutoPrompt, genCostNote, composeCostNote, setEditDirty,stepState, jobs, failures, onActionDone, onJobsRefresh }) => {
  const alive = useAliveRef();
  const [err, setErr] = useState<ActionError | null>(null);
  const [deleting, setDeleting] = useState(false);
  // 261010 P2c-2: kết quả móc "duyệt ⇒ tự dựng prompt" của lần duyệt vừa rồi (reset khi đổi video / bỏ duyệt).
  const [autoPrompt, setAutoPrompt] = useState<AutoPrompt | null>(null);
  useEffect(() => { setAutoPrompt(null); }, [eventId]);
  const sb = storyboards?.find((s) => s.eventId === eventId)?.storyboard;

  // `isDirty` do chính khối storyboard giữ (bản nháp khung) và tự khoá nút Gen/Duyệt khi có sửa chưa lưu ⇒ ở đây tính với `false`.
  const ctx: WorkflowActionCtx = useMemo(() => ({
    isSky: true, stepState, runningJobKinds: runningKindsOf(jobs, eventId), storyboard: storyboardCtxOf(sb, ev, false),
  }), [stepState, jobs, eventId, sb, ev]);
  const actions = useMemo(() => actionsFor('STORYBOARD', ctx), [ctx]);
  const del = actionOf(actions, 'SB_DELETE');
  const running = ctx.runningJobKinds.includes('FILM_STORYBOARD');
  // job dựng prompt của CHÍNH video này còn chạy? (job cấp video: `eventId` khớp; job cấp plan không `eventId` cũng làm bước 7 bận ⇒ tính cả hai, cùng `runningKindsOf`)
  const authorRunning = ctx.runningJobKinds.includes('FILM_AUTHOR_PROMPTS');
  const autoLine = autoPromptLineOf(autoPrompt, authorRunning);

  const sbCtx: StoryboardCtx = useMemo(() => ({
    byEvent: Object.fromEntries((storyboards || []).filter((s) => s.storyboard).map((s) => [s.eventId, s.storyboard!])),
    loaded: storyboards !== null, loading: false, loadError: storyboardsError,
    pos: maps ? { links: maps.links.valid, maps: maps.maps.map((m) => ({ id: m.id, name: m.name })) } : null,
    jobs, failures,
    onReload: async () => { onActionDone(); },
    onJobsRefresh,
  }), [storyboards, storyboardsError, maps, jobs, failures, onActionDone, onJobsRefresh]);

  const doDelete = async () => {
    setErr(null); setDeleting(true);
    try { await filmApi.deleteStoryboard(eventId); }
    catch (e: any) { if (alive.current) setErr(workflowActionError(e, 'Xoá storyboard')); }
    finally { if (alive.current) setDeleting(false); onActionDone(); }
  };
  const delConfirm = confirmContent('DELETE', 'SB');

  if (storyboards === null && !storyboardsError) return <div style={{ padding: 8 }}><Spin size="small" /> <span className="muted">Đang tải storyboard…</span></div>;

  return (
    <div data-testid="wf-storyboard-step">
      <StepErrorAlert error={err} onReload={onActionDone} onClose={() => setErr(null)} />
      {storyboardsError && <Alert type="error" showIcon style={{ marginBottom: 8 }} message={`Không tải được storyboard: ${storyboardsError}`} action={<Button size="small" onClick={onActionDone}>Tải lại</Button>} />}
      {running && <div style={{ marginBottom: 8 }}><RunningTag>storyboard đang chạy ở nền…</RunningTag></div>}
      {autoLine && <div className="wf-alert INFO" role="status" data-testid="wf-auto-prompt">{autoLine}</div>}
      <StoryboardBlock onDirtyChange={setEditDirty} ev={ev} ctx={sbCtx} hideCost chipPrompt alwaysOpen noAutoPrompt={noAutoPrompt} genCostNote={genCostNote} composeCostNote={composeCostNote} onActionError={(e) => { setErr(e); onActionDone(); }}
        onApproved={(ap) => { // started: hỏi jobs NGAY rồi mới hiện dòng, để không chớp "đã kết thúc" trước khi thấy job
          if (ap?.started) void onJobsRefresh().finally(() => { if (alive.current) setAutoPrompt(ap); });
          else setAutoPrompt(ap ?? null);
        }}
        policy={{
          compose: actionOf(actions, 'SB_COMPOSE'), generate: actionOf(actions, 'SB_GENERATE'), save: actionOf(actions, 'SB_SAVE_FRAMES'),
          approve: actionOf(actions, 'SB_APPROVE'), unapprove: actionOf(actions, 'SB_UNAPPROVE'),
        }} />
      <StoryboardCaptions frames={sb?.frames} shots={ev.shots} />
      {del?.visible && (
        <Space style={{ marginTop: 10 }}>
          <Popconfirm title={delConfirm.title} description={<div style={{ maxWidth: 300 }}>{delConfirm.body}</div>} okText={delConfirm.okText} cancelText={delConfirm.cancelText}
            okButtonProps={{ danger: true }} disabled={!del.enabled} onConfirm={doDelete}>
            <Button size="small" danger loading={deleting} disabled={!del.enabled} title={del.enabled ? undefined : del.reason} data-testid="wf-sb-delete">{del.label}</Button>
          </Popconfirm>
        </Space>
      )}
    </div>
  );
};

export default WorkflowStoryboardStep;
